import { Inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ENVIRONMENT } from './environment.token';
import { HttpClient } from '@angular/common/http';

export interface NotificationData {
  MessageType: string;
  MessageCount: number;
  PostCount: number;
  NotificationCount: number;
  NewMessage?: ChatMessageToastDto
}

export interface ChatMessageToastDto {
  Id: number;
  ChatId: number,
  SenderId: number;
  SenderUserName: string;
  HasAttachments: boolean;
  IsGroup: boolean;
  CreatedDateUTC: string;
}

export interface CriticalPostsData {
  MessageType: string;
  PostCount: number;
  UpdatedAt: Date;
  PostIds: number[];
}

/** Notification badge counts keyed by AppId. Covers all apps, not just nav-row ones. */
export type AppNotificationCounts = Record<number, number>;

export interface GlobalUpdatesResponse {
  Notifications: NotificationData;
  AppCounts: AppNotificationCounts;
  CriticalPosts: CriticalPostsData;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalPollingService {
  private pollInterval: any = null;
  private notificationSubject = new BehaviorSubject<NotificationData | null>(null);
  private criticalPostsSubject = new BehaviorSubject<CriticalPostsData | null>(null);
  private appCountsSubject = new BehaviorSubject<AppNotificationCounts | null>(null);
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  // Emits whenever polling is paused (true) or resumed (false). Other component-level
  // pollers (e.g. the messages page chat poll) subscribe to this so they pause/resume
  // in lockstep with the global poller instead of running independently in the background.
  private pauseStateSubject = new BehaviorSubject<boolean>(false);
  private apiUrl: string;
  private isPaused = false;
  // Guard so we never stack a second GetGlobalUpdates request on top of an in-flight one.
  // poll() is invoked from several places (scheduled cycle, markUserActivity,
  // enableFastPolling, resumePolling, connect); without this guard those callers could
  // overlap and fire duplicate requests while a previous one is still pending.
  private isPolling = false;
  // Throttle for global interaction listeners so a burst of clicks/keystrokes doesn't
  // hammer markUserActivity (which can trigger a poll when leaving the idle interval).
  private lastActivityMarkAt = 0;
  private readonly ACTIVITY_THROTTLE = 5000; // 5s
  private currentUserId: number = 0;
  private isConnecting: boolean = false; // Guard to prevent multiple simultaneous connect() calls

  // ========== ADAPTIVE POLLING CONFIGURATION ==========
  // Adaptive polling adjusts the polling interval based on user activity to:
  // 1. Reduce server load when user is idle (30s interval instead of 10s)
  // 2. Provide faster updates during active chat (5s interval)
  // 3. Return to normal polling (10s) for general app usage

  private currentInterval: number = 10000; // Current polling interval (starts at 10s)
  private lastActivityTime: number = Date.now(); // Timestamp of last user interaction

  // Polling interval constants (in milliseconds)
  private readonly FAST_INTERVAL = 5000;        // 5s - Used during active chat for near real-time updates
  private readonly NORMAL_INTERVAL = 10000;     // 10s - Default interval for general app usage
  private readonly IDLE_INTERVAL = 30000;       // 30s - Used when user is idle to reduce server load

  // Activity threshold (in milliseconds)
  private readonly IDLE_THRESHOLD = 60000;      // 1 minute - Time without activity before switching to idle interval

  private isFastPollingEnabled = false;         // Flag to track if fast polling mode is active (for chat pages)

  constructor(
    private ngZone: NgZone,
    private http: HttpClient,
    @Inject(ENVIRONMENT) environment: any
  ) {
    this.apiUrl = environment.apiUrl;
    this.setupBrowserTabVisibilityListener();
    this.setupActivityListeners();
  }

  private setupBrowserTabVisibilityListener(): void {
    // Handle browser tab visibility changes (web only - native apps use Capacitor's appStateChange)
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pausePolling();
        } else {
          this.resumePolling();
        }
      });
    }
  }

  private setupActivityListeners(): void {
    // Treat any user interaction (not just sending a message) as activity, so an
    // actively-reading user doesn't get demoted to the 30s idle interval after 1 minute.
    // Throttled so a burst of events doesn't repeatedly trigger markUserActivity.
    if (typeof document === 'undefined') {
      return;
    }
    const onInteraction = () => {
      const now = Date.now();
      if (now - this.lastActivityMarkAt >= this.ACTIVITY_THROTTLE) {
        this.lastActivityMarkAt = now;
        this.markUserActivity();
      }
    };
    document.addEventListener('click', onInteraction, { passive: true });
    document.addEventListener('keydown', onInteraction, { passive: true });
    document.addEventListener('touchstart', onInteraction, { passive: true });
  }

  /**
   * The no-login surfaces (/guest/... and the public post page /p/...) have no Okta session, so
   * global polling must never run there — otherwise GetGlobalUpdates 401s on a loop. Checked at
   * call time so a cycle started before navigating here also stands down (see poll()).
   */
  private isGuestContext(): boolean {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname;
    return path.startsWith('/guest') || path.startsWith('/p/');
  }

  async connect(userId: number): Promise<void> {
    // Never poll on the public guest page (no auth).
    if (this.isGuestContext()) {
      return;
    }

    // Guard against multiple simultaneous connect() calls from different components
    // This prevents race conditions where header, footer, and critical-alerts all call connect() at the same time
    if (this.isConnecting) {
      return;
    }

    // If already connected to the same user, skip reconnection
    if (this.pollInterval && this.currentUserId === userId) {
      
      return;
    }

    this.isConnecting = true;

    try {
      // Disconnect existing polling before starting new connection
      if (this.pollInterval) {
        this.disconnect();
      }

      this.currentUserId = userId;
      this.connectionStatusSubject.next(true);

      // Mark connection as user activity to start with normal interval
      this.lastActivityTime = Date.now();

      // Poll immediately to get latest data
      await this.poll();

      // Start adaptive polling cycle
      this.scheduleNextPoll();

      
    } finally {
      this.isConnecting = false;
    }
  }

  private async poll(): Promise<void> {
    // If we've landed on the public guest page, tear down any cycle that was already running
    // (this service is a root singleton, so a cycle started pre-navigation would otherwise keep
    // 401ing against GetGlobalUpdates with no Okta token).
    if (this.isGuestContext()) {
      this.disconnect();
      return;
    }

    // Don't stack a new request on top of one that's still pending. Any caller that
    // arrives while a poll is in flight is dropped — the in-flight request will deliver
    // the latest data, and the regular cycle will continue from there.
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;
    try {
      const data = await this.http.get<GlobalUpdatesResponse>(
        `${this.apiUrl}/NotificationPolling/GetGlobalUpdates`
      ).toPromise();

      if (data) {
        this.ngZone.run(() => {
          // Emit notifications
          this.notificationSubject.next(data.Notifications);

          // Emit critical posts
          this.criticalPostsSubject.next(data.CriticalPosts);

          // Emit per-app nav-row badges. `?? {}` so a client running against an older
          // server clears its badges rather than freezing them at the last known values.
          this.appCountsSubject.next(data.AppCounts ?? {});
        });
      }
    } catch (error) {
      console.error('Error polling global updates:', error);
      this.connectionStatusSubject.next(false);

      // Retry after delay
      setTimeout(() => {
        if (this.pollInterval) {
          this.connectionStatusSubject.next(true);
        }
      }, 5000);
    } finally {
      this.isPolling = false;
    }
  }

  /**
   * Schedules the next poll based on current activity state
   * This is the core of adaptive polling - it determines the interval dynamically
   */
  private scheduleNextPoll(): void {
    // Clear any existing timeout to avoid duplicate polls
    if (this.pollInterval) {
      clearTimeout(this.pollInterval);
    }

    // Determine polling interval based on activity and mode
    if (this.isFastPollingEnabled) {
      // Fast polling mode (active chat) - poll every 5 seconds for near real-time updates
      this.currentInterval = this.FAST_INTERVAL;
    } else {
      // Calculate time since last user activity
      const timeSinceActivity = Date.now() - this.lastActivityTime;

      if (timeSinceActivity < this.IDLE_THRESHOLD) {
        // User has been active recently - use normal 10s interval
        this.currentInterval = this.NORMAL_INTERVAL;
      } else {
        // User has been idle for 1+ minute - use slower 30s interval to reduce server load
        // This saves ~67% of server requests during idle periods
        this.currentInterval = this.IDLE_INTERVAL;
      }
    }

    // Schedule the next poll with the calculated interval
    this.pollInterval = setTimeout(() => {
      if (!this.isPaused) {
        // Execute poll and schedule the next one
        this.poll().then(() => this.scheduleNextPoll());
      } else {
        // If paused (app backgrounded), reschedule to check again later
        this.scheduleNextPoll();
      }
    }, this.currentInterval);
  }

  /**
   * Marks user activity to reset idle timer
   * Call this when user performs any interaction (sends message, clicks notification, etc.)
   * This ensures polling switches from idle (30s) back to normal (10s) interval
   */
  public markUserActivity(): void {
    this.lastActivityTime = Date.now();

    // If we're currently in idle mode, immediately poll and reset to normal interval
    // This ensures user gets fresh data quickly after returning from idle state
    if (this.currentInterval === this.IDLE_INTERVAL) {
      this.poll();
      this.scheduleNextPoll();
    }
  }

  /**
   * Enables fast polling mode (5s interval) for active chat scenarios
   * Call this when user opens the messages page to get near real-time message updates
   */
  public enableFastPolling(): void {
    this.isFastPollingEnabled = true;
    this.lastActivityTime = Date.now();

    // Immediately poll and reschedule with fast interval
    this.poll();
    this.scheduleNextPoll();
  }

  /**
   * Disables fast polling mode and returns to activity-based intervals
   * Call this when user leaves the messages page
   */
  public disableFastPolling(): void {
    this.isFastPollingEnabled = false;
    this.lastActivityTime = Date.now();

    // Reschedule with normal interval
    this.scheduleNextPoll();
  }

  disconnect(): void {
    if (this.pollInterval) {
      clearTimeout(this.pollInterval);
      this.pollInterval = null;
      this.connectionStatusSubject.next(false);
      
    }
    this.currentUserId = 0;
    this.isConnecting = false; // Reset guard
  }

  pausePolling(): void {
    this.isPaused = true;
    this.pauseStateSubject.next(true);
  }

  resumePolling(): void {
    this.isPaused = false;
    this.pauseStateSubject.next(false);

    // Mark as activity so we resume with normal interval (not idle)
    this.lastActivityTime = Date.now();

    // Immediate catch-up poll to get latest data after app resume
    this.poll().then(() => {
      // Restart adaptive polling cycle
      this.scheduleNextPoll();
    });
  }

  /**
   * Emits true when polling is paused (app backgrounded / tab hidden) and false when
   * resumed. Component-level pollers subscribe to stay in lockstep with the global poller.
   */
  getPauseState(): Observable<boolean> {
    return this.pauseStateSubject.asObservable();
  }

  getNotifications(): Observable<NotificationData | null> {
    return this.notificationSubject.asObservable();
  }

  getAppCounts(): Observable<AppNotificationCounts | null> {
    return this.appCountsSubject.asObservable();
  }

  getCriticalPosts(): Observable<CriticalPostsData | null> {
    return this.criticalPostsSubject.asObservable();
  }

  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatusSubject.asObservable();
  }

  isConnected(): boolean {
    return this.pollInterval !== null;
  }
}
